import axios from 'axios'
import { apiClient } from './client'
import type { QaValidationSummary, QaPolicy, UpsertQaValidationRequest, ApiResponse, ConfigurationDrift } from '@/types'

/**
 * Platform administration surfaces: Organizations CRUD, User Management
 * (list/detail/role/org/platform-admin/tenant-assignment/deactivate/
 * pre-register/welcome-email), Admin Consent (multi-tenant auth), the
 * DevOps Integration / Config-as-Code pull-request surfaces, and QA policy
 * validation. Split out of `lib/api.ts` (WO-10.4).
 */

// ============================================================================
// Organizations types — mirror IuvoGuardian.Core/DTOs/Organizations/OrganizationDto.cs
// (camelCase here, PascalCase on the wire; ASP.NET JSON serializer does the bridge).
// Status lifecycle: 'Active' | 'PendingApproval' | 'Inactive' | 'Rejected'.
// ============================================================================

export type OrganizationStatus = 'Active' | 'PendingApproval' | 'Inactive' | 'Rejected'
export type OrganizationType = 'Platform' | 'MSP' | 'Customer'

export interface OrganizationListItemDto {
  id: string
  name: string
  slug: string
  type: string
  azureAdTenantId?: string | null
  parentOrganizationId?: string | null
  parentOrganizationName?: string | null
  primaryContactEmail?: string | null
  primaryContactName?: string | null
  domain?: string | null
  isActive: boolean
  status: string
  allowSelfSignup: boolean
  allowAutoProvision: boolean
  maxUsers?: number | null
  maxTenants?: number | null
  linkedUserCount: number
  linkedTenantCount: number
  createdAt: string
  updatedAt: string
}

export interface OrganizationDetailDto extends OrganizationListItemDto {
  /** Open-schema JSON blob for org-specific configuration. Server returns string-or-null. */
  settings?: string | null
}

export interface CreateOrganizationRequest {
  name: string
  slug: string
  azureAdTenantId?: string | null
  type: string
  parentOrganizationId?: string | null
  primaryContactEmail?: string | null
  primaryContactName?: string | null
  domain?: string | null
  isActive?: boolean
  allowSelfSignup?: boolean
  allowAutoProvision?: boolean
  maxUsers?: number | null
  maxTenants?: number | null
  settings?: string | null
}

export interface UpdateOrganizationRequest {
  name?: string
  slug?: string
  azureAdTenantId?: string | null
  type?: string
  parentOrganizationId?: string | null
  primaryContactEmail?: string | null
  primaryContactName?: string | null
  domain?: string | null
  isActive?: boolean
  allowSelfSignup?: boolean
  allowAutoProvision?: boolean
  maxUsers?: number | null
  maxTenants?: number | null
  settings?: string | null
}

export interface PagedOrganizationsResponse {
  items: OrganizationListItemDto[]
  totalCount: number
  page: number
  pageSize: number
}

// Organizations APIs (Platform-Admin-only — auth enforced server-side).
// The Function App proxies to the Container at /api/organizations/*.
// by-tid/{tid} returns 200 with the row (any Status) or null when 404 —
// the wrapper swallows the 404 so callers can use `if (org === null)`.
export async function getOrganizations(
  params?: { page?: number; pageSize?: number; status?: string; type?: string },
): Promise<PagedOrganizationsResponse> {
  const response = await apiClient.get<PagedOrganizationsResponse>('/organizations', {
    params,
  })
  return response.data
}

export async function getOrganizationById(id: string): Promise<OrganizationDetailDto> {
  const response = await apiClient.get<OrganizationDetailDto>(`/organizations/${id}`)
  return response.data
}

export async function getOrganizationByTid(tid: string): Promise<OrganizationDetailDto | null> {
  try {
    const response = await apiClient.get<OrganizationDetailDto>(`/organizations/by-tid/${tid}`)
    return response.data
  } catch (err) {
    // 404 is a normal "no record" response for the inline-detection lookup.
    // Caller distinguishes a missing org from a transport error via null.
    if (axios.isAxiosError(err) && err.response?.status === 404) {
      return null
    }
    throw err
  }
}

export async function createOrganization(data: CreateOrganizationRequest): Promise<OrganizationDetailDto> {
  const response = await apiClient.post<OrganizationDetailDto>('/organizations', data)
  return response.data
}

export async function updateOrganization(
  id: string,
  data: Partial<UpdateOrganizationRequest>,
): Promise<OrganizationDetailDto> {
  const response = await apiClient.put<OrganizationDetailDto>(`/organizations/${id}`, data)
  return response.data
}

export async function deactivateOrganization(id: string, reason?: string): Promise<void> {
  await apiClient.post(`/organizations/${id}/deactivate`, reason ? { reason } : {})
}

// Reject an intake submission (PendingApproval queue). Distinct from
// deactivate: commits Status='Rejected' so the signup recycle path can
// reuse the row if the same company re-submits via /signup.
export async function rejectOrganization(id: string, reason?: string): Promise<void> {
  await apiClient.post(`/organizations/${id}/reject`, reason ? { reason } : {})
}

export async function approveOrganization(
  id: string,
  opts?: { allowAutoProvision?: boolean; allowSelfSignup?: boolean },
): Promise<OrganizationDetailDto> {
  const response = await apiClient.post<OrganizationDetailDto>(
    `/organizations/${id}/approve`,
    opts ?? {},
  )
  return response.data
}

// ============================================================================
// User Management types — mirror IuvoGuardian.Container/Controllers/
// UserManagementController.cs DTOs (camelCase on the wire).
// ============================================================================

export interface CurrentUserDto {
  objectId: string
  email: string
  displayName: string
  role: string
  isGlobalAdmin: boolean
  isPlatformAdmin: boolean
  accessibleTenantIds: string[]
  isActive: boolean
}

// Optional fields are plain `string?` (no `| null`) — the Container's JSON
// options set `DefaultIgnoreCondition = WhenWritingNull` (`Program.cs:93`),
// so a null C# property is OMITTED from the response, never sent as a
// literal JSON `null`. `string | undefined` is the accurate wire type.
export interface UserManagementListItem {
  id: string
  objectId: string
  email: string
  displayName: string
  role: string
  isActive: boolean
  createdAt: string
  lastLoginAt: string
  welcomeEmailSentAt?: string
  accessibleTenantCount: number
  accessibleTenantIds: string[]
  organizationId?: string
  organizationName?: string
  isPlatformAdmin: boolean
}

export interface UserManagementDetail {
  id: string
  objectId: string
  email: string
  displayName: string
  role: string
  isActive: boolean
  createdAt: string
  lastLoginAt: string
  accessibleTenantIds: string[]
}

export interface PreRegisterUserPayload {
  objectId: string
  homeTenantId: string
  email: string
  displayName?: string
  role: string
  tenantIds?: string[]
}

// User Management APIs
export async function getCurrentUser(): Promise<CurrentUserDto> {
  const response = await apiClient.get<CurrentUserDto>('/usermanagement/me')
  return response.data
}

export async function getUsers(): Promise<UserManagementListItem[]> {
  const response = await apiClient.get<UserManagementListItem[]>('/usermanagement')
  return response.data
}

export async function getTenantUsers(tenantId: string): Promise<UserManagementListItem[]> {
  const response = await apiClient.get<UserManagementListItem[]>(`/usermanagement/tenant/${tenantId}`)
  return response.data
}

export async function getUser(userObjectId: string): Promise<UserManagementDetail> {
  const response = await apiClient.get<UserManagementDetail>(`/usermanagement/${userObjectId}`)
  return response.data
}

export async function assignUserToTenant(userObjectId: string, tenantId: string): Promise<{ message: string }> {
  const response = await apiClient.post<{ message: string }>(`/usermanagement/${userObjectId}/tenants/${tenantId}`)
  return response.data
}

export async function removeUserFromTenant(userObjectId: string, tenantId: string): Promise<void> {
  await apiClient.delete(`/usermanagement/${userObjectId}/tenants/${tenantId}`)
}

export async function updateUserRole(userObjectId: string, role: string): Promise<{ message: string }> {
  const response = await apiClient.put<{ message: string }>(`/usermanagement/${userObjectId}/role`, { role })
  return response.data
}

// Reassign a user's owning organization. Returns 204 NoContent on success.
// The HTTP error → message mapping below mirrors the Wave A2 controller
// contract: 400 self-demotion / model invalid, 403 caller not GlobalAdmin,
// 404 user or org missing, 409 foreign-tenant ObjectId collision. The 400
// body shape is `{ message }` for the self-demotion case and ModelState
// for an invalid body — fall back to a calm generic if neither is present.
export async function updateUserOrganization(userObjectId: string, organizationId: string): Promise<void> {
  try {
    await apiClient.put(`/usermanagement/${userObjectId}/organization`, { organizationId })
  } catch (err) {
    if (axios.isAxiosError(err) && err.response) {
      const status = err.response.status
      const body = err.response.data
      const serverMessage = typeof body === 'string'
        ? body
        : (body?.message ?? body?.Message ?? null)
      switch (status) {
        case 400:
          throw new Error(serverMessage || 'This change is not allowed for that user.')
        case 403:
          throw new Error("You don't have permission to make this change.")
        case 404:
          throw new Error('User or organization not found.')
        case 409:
          throw new Error('This user already exists in another tenant. Resolve the conflict before reassigning.')
        case 500:
        default:
          throw new Error('Something went wrong. Please try again.')
      }
    }
    throw err
  }
}

// Grant or revoke Platform Admin in one atomic action. Returns 204 NoContent
// on success. The HTTP error → message mapping mirrors updateUserOrganization
// (same Container contract): 400 self-revoke / model invalid, 403 caller not
// GlobalAdmin, 404 user missing, 409 foreign-tenant ObjectId collision. The
// 400 body is `{ message }` for the self-revoke case and ModelState for an
// invalid body — fall back to a calm generic if neither is present.
export async function setPlatformAdmin(userObjectId: string, granted: boolean): Promise<void> {
  try {
    await apiClient.put(`/usermanagement/${userObjectId}/platform-admin`, { granted })
  } catch (err) {
    if (axios.isAxiosError(err) && err.response) {
      const status = err.response.status
      const body = err.response.data
      const serverMessage = typeof body === 'string'
        ? body
        : (body?.message ?? body?.Message ?? null)
      switch (status) {
        case 400:
          throw new Error(serverMessage || 'This change is not allowed for that user.')
        case 403:
          throw new Error("You don't have permission to make this change.")
        case 404:
          throw new Error('User not found.')
        case 409:
          throw new Error('This user already exists in another tenant. Resolve the conflict before changing platform access.')
        case 500:
        default:
          throw new Error('Something went wrong. Please try again.')
      }
    }
    throw err
  }
}

export async function deactivateUser(userObjectId: string): Promise<{ message: string }> {
  const response = await apiClient.post<{ message: string }>(`/usermanagement/${userObjectId}/deactivate`)
  return response.data
}

export async function preRegisterUser(userData: PreRegisterUserPayload): Promise<UserManagementDetail> {
  const response = await apiClient.post<UserManagementDetail>('/usermanagement/pre-register', userData)
  return response.data
}

export async function sendWelcomeEmail(userObjectId: string): Promise<{
  success: boolean
  message: string
  sentTo: string
  sentAt: string
}> {
  const response = await apiClient.post(`/usermanagement/${userObjectId}/send-welcome-email`)
  return response.data
}

// ============================================================================
// DevOps Integration + Config-as-Code Pull Request types
// ============================================================================

export interface DevOpsPullRequest {
  id: string
  tenantId: string
  pullRequestId: string
  pullRequestUrl: string
  title: string
  description?: string
  branchName?: string
  status: 'Active' | 'Merged' | 'Abandoned'
  triggeredByEmail?: string
  triggeredByDisplayName?: string
  driftCount: number
  createdAt: string
  mergedAt?: string
  mergedBy?: string
  drifts?: DevOpsPullRequestDrift[]
}

export interface DevOpsPullRequestDrift {
  driftId: string
  component: string
  setting: string
  approvedBy?: string
  approvedAt?: string
  severity?: string
}

/** `GET /devopsintegration/configuration` response (anonymous camelCase object). */
export interface DevOpsConfiguration {
  Organization?: string | null
  Project?: string | null
  Repository?: string | null
  BaselineDirectory: string
  HasPAT: boolean
  CreatePullRequests: boolean
}

// DevOps Integration APIs
export async function validateDevOpsConnection(): Promise<{ isValid: boolean; message: string }> {
  const response = await apiClient.get<ApiResponse<{ isValid: boolean; message: string }>>('/devopsintegration/validate-connection')
  return response.data.data
}

export async function getDevOpsConfiguration(): Promise<DevOpsConfiguration> {
  const response = await apiClient.get<ApiResponse<DevOpsConfiguration>>('/devopsintegration/configuration')
  return response.data.data
}

/**
 * Trigger baseline-update follow-up for approved drift.
 *
 * No backend route matches `/devopsintegration/trigger-baseline-update` in
 * the current Container (`DevOpsIntegrationController` has no such action)
 * — this call would 404 in production today. There are no callers of this
 * client method anywhere in the frontend, so it is a dead, unreachable
 * relic; left in place (out of scope for this split) and typed `unknown`
 * since there's no real endpoint contract to verify a shape against.
 */
export async function triggerBaselineUpdate(tenantId: string, approvedChanges: ConfigurationDrift[]): Promise<unknown> {
  const response = await apiClient.post<{ data: unknown }>(
    '/devopsintegration/trigger-baseline-update',
    { tenantId, approvedChanges }
  )
  return response.data.data
}

export async function getDevOpsPullRequests(params: {
  tenantId: string
  page?: number
  pageSize?: number
  status?: string
}): Promise<{
  items: DevOpsPullRequest[]
  totalCount: number
  page: number
  pageSize: number
  totalPages: number
  hasNextPage: boolean
  hasPreviousPage: boolean
}> {
  const response = await apiClient.get('/devopspullrequests', { params })
  return response.data?.data || response.data
}

export async function getDevOpsPullRequest(id: string, tenantId: string): Promise<DevOpsPullRequest | null> {
  try {
    const response = await apiClient.get(`/devopspullrequests/${id}`, {
      params: { tenantId }
    })
    return response.data?.data || response.data
  } catch (error: unknown) {
    if (axios.isAxiosError(error) && error.response?.status === 404) {
      return null
    }
    throw error
  }
}

export async function getDevOpsPullRequestDrifts(id: string, tenantId: string): Promise<DevOpsPullRequestDrift[]> {
  try {
    const response = await apiClient.get(`/devopspullrequests/${id}/drifts`, {
      params: { tenantId }
    })
    return response.data?.data || response.data || []
  } catch (error: unknown) {
    if (axios.isAxiosError(error) && error.response?.status === 404) {
      return []
    }
    throw error
  }
}

export async function syncDevOpsPullRequestStatus(id: string, tenantId: string): Promise<void> {
  await apiClient.post(`/devopspullrequests/${id}/sync`, null, {
    params: { tenantId }
  })
}

export async function syncAllDevOpsPullRequests(tenantId: string): Promise<void> {
  await apiClient.post('/devopspullrequests/sync-all', null, {
    params: { tenantId }
  })
}

// ============================================================================
// Admin Consent (Multi-Tenant Authentication) types
// ============================================================================

export interface AdminConsentInitiateResponse {
  consentUrl: string
  tenantId: string
  tenantName: string
  expiresInMinutes: number
}

export interface AdminConsentCallbackRequest {
  tenantId: string | null
  state: string
  adminConsent?: string
  error?: string
  errorDescription?: string
}

/** `errorCode` for a consent link whose 15-minute state expired (OB-05). */
export const CONSENT_LINK_EXPIRED = 'ConsentLinkExpired'

export interface AdminConsentCallbackResponse {
  success: boolean
  tenantId: string
  tenantName?: string
  authenticationMode?: string
  permissionsGranted?: number
  message?: string
  errorMessage?: string
  /** Machine-readable failure reason; `ConsentLinkExpired` when the link's state timed out. */
  errorCode?: string
}

export interface AdminConsentStatus {
  tenantId: string
  tenantName: string
  authenticationMode: 'PerTenant' | 'MultiTenant'
  isMultiTenantEnabled: boolean
  consentStatus: {
    isValid: boolean
    grantedAt?: string
    grantedBy?: string
    errorMessage?: string
  }
  adminConsentRevoked: boolean
  adminConsentRevokedAt?: string
  servicePrincipalObjectId?: string
}

export interface AdminConsentRevokeResponse {
  success: boolean
  tenantId: string
  tenantName: string
  authenticationMode: string
  message: string
  timestamp: string
}

// Onboarding Email types
export interface SendOnboardingEmailRequest {
  recipientEmail: string
  recipientName: string
}

export interface SendOnboardingEmailResponse {
  success: boolean
  message: string
  sentTo: string
  sentAt: string
}

// Admin Consent APIs (Multi-Tenant Authentication)
export async function initiateAdminConsent(tenantId: string): Promise<AdminConsentInitiateResponse> {
  const response = await apiClient.post<AdminConsentInitiateResponse>(`/tenant-consent/${tenantId}/initiate`)
  return response.data
}

export async function processAdminConsentCallback(request: AdminConsentCallbackRequest): Promise<AdminConsentCallbackResponse> {
  const response = await apiClient.post<AdminConsentCallbackResponse>('/tenant-consent/callback', request)
  return response.data
}

export async function getAdminConsentStatus(tenantId: string): Promise<AdminConsentStatus> {
  const response = await apiClient.get<AdminConsentStatus>(`/tenant-consent/${tenantId}/status`)
  return response.data
}

export async function verifyAndRecordConsent(tenantId: string): Promise<AdminConsentCallbackResponse> {
  const response = await apiClient.post<AdminConsentCallbackResponse>(`/tenant-consent/${tenantId}/verify-and-record`)
  return response.data
}

export async function revokeAdminConsent(tenantId: string): Promise<AdminConsentRevokeResponse> {
  const response = await apiClient.post<AdminConsentRevokeResponse>(`/tenant-consent/${tenantId}/revoke`)
  return response.data
}

// Onboarding Email API
export async function sendOnboardingEmail(
  tenantId: string,
  request: SendOnboardingEmailRequest
): Promise<SendOnboardingEmailResponse> {
  const response = await apiClient.post<SendOnboardingEmailResponse>(
    `/tenant-consent/${tenantId}/send-onboarding-email`,
    request
  )
  return response.data
}

// QA Policy Validation APIs
export async function getQaValidation(): Promise<QaValidationSummary> {
  const response = await apiClient.get('/qa-validation')
  return response.data
}

export async function upsertQaValidation(policyEntityId: string, body: UpsertQaValidationRequest): Promise<QaPolicy> {
  const response = await apiClient.put(`/qa-validation/${policyEntityId}`, body)
  return response.data
}

export const adminApi = {
  getOrganizations,
  getOrganizationById,
  getOrganizationByTid,
  createOrganization,
  updateOrganization,
  deactivateOrganization,
  rejectOrganization,
  approveOrganization,
  getCurrentUser,
  getUsers,
  getTenantUsers,
  getUser,
  assignUserToTenant,
  removeUserFromTenant,
  updateUserRole,
  updateUserOrganization,
  setPlatformAdmin,
  deactivateUser,
  preRegisterUser,
  sendWelcomeEmail,
  validateDevOpsConnection,
  getDevOpsConfiguration,
  triggerBaselineUpdate,
  getDevOpsPullRequests,
  getDevOpsPullRequest,
  getDevOpsPullRequestDrifts,
  syncDevOpsPullRequestStatus,
  syncAllDevOpsPullRequests,
  initiateAdminConsent,
  processAdminConsentCallback,
  getAdminConsentStatus,
  verifyAndRecordConsent,
  revokeAdminConsent,
  sendOnboardingEmail,
  getQaValidation,
  upsertQaValidation,
}
